import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchProjectTimeline } from '@/service/api/project';
import { formatDate } from './utils';
import Select from '@/components/common/Select';

interface TimelineTabProps {
  project: Api.Project.ProjectDetailResponse;
}

const TYPE_OPTIONS = [
  { label: '全部事件类型', value: '' },
  { label: '项目状态', value: 'PROJECT' },
  { label: '剧集审核', value: 'EPISODE' },
  { label: '渠道发行', value: 'CHANNEL' },
  { label: '合同归档', value: 'CONTRACT' },
  { label: '收益账单', value: 'STATEMENT' },
  { label: '结算记录', value: 'SETTLEMENT' }
];

export default function TimelineTab({ project }: TimelineTabProps) {
  const projectId = project?.id || '';

  const [typeFilter, setTypeFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState<Api.Project.TimelineEventResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // 用于记忆上次请求参数 key，防止重复请求
  const lastFetchKeyRef = useRef<string>('');

  const loadTimeline = useCallback(async (isRefresh = false) => {
    if (!projectId) return;

    const targetPage = isRefresh ? 1 : page;
    const requestKey = `${projectId}_${typeFilter}_${targetPage}_${pageSize}`;
    if (!isRefresh && lastFetchKeyRef.current === requestKey) {
      return;
    }
    lastFetchKeyRef.current = requestKey;

    setIsLoading(true);
    try {
      const res = await fetchProjectTimeline(projectId, {
        types: typeFilter || undefined,
        page: targetPage,
        page_size: pageSize
      });

      const newItems = res?.items || [];
      const newTotal = res?.total || 0;

      setTotal(newTotal);
      if (targetPage === 1) {
        setItems(newItems);
      } else {
        setItems((prev) => [...prev, ...newItems]);
      }
    } catch (err) {
      console.error('获取项目进度时间线失败:', err);
      lastFetchKeyRef.current = '';
    } finally {
      setIsLoading(false);
    }
  }, [projectId, typeFilter, page, pageSize]);

  useEffect(() => {
    loadTimeline();
  }, [loadTimeline]);

  // 类型筛选变更
  const handleTypeChange = (val: string | number) => {
    setTypeFilter(String(val));
    setPage(1);
    lastFetchKeyRef.current = '';
  };

  // 手动刷新按钮
  const handleRefresh = () => {
    if (page !== 1) {
      setPage(1);
    }
    loadTimeline(true);
  };

  // 滚动触底检测加载更多
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 40) {
      if (!isLoading && items.length < total) {
        setPage((prev) => prev + 1);
      }
    }
  };

  /** 严重程度 Severity 徽章 */
  const renderSeverityBadge = (severity?: string) => {
    switch (severity?.toUpperCase()) {
      case 'ERROR':
      case 'HIGH':
      case 'CRITICAL':
        return <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-50 text-rose-600 border border-rose-200/80 font-medium">高/异常</span>;
      case 'WARNING':
      case 'MEDIUM':
        return <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200/80 font-medium">警告</span>;
      case 'INFO':
      case 'LOW':
      default:
        return <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200/80 font-medium">通知</span>;
    }
  };

  /** 事件类型图标及颜色样式映射 */
  const getEventStyle = (type?: string) => {
    switch (type?.toUpperCase()) {
      case 'PROJECT':
        return { icon: 'ri-folder-3-line', bg: 'bg-purple-50 text-purple-600 border-purple-200' };
      case 'EPISODE':
        return { icon: 'ri-clapperboard-line', bg: 'bg-blue-50 text-blue-600 border-blue-200' };
      case 'CHANNEL':
        return { icon: 'ri-global-line', bg: 'bg-indigo-50 text-indigo-600 border-indigo-200' };
      case 'CONTRACT':
        return { icon: 'ri-file-text-line', bg: 'bg-teal-50 text-teal-600 border-teal-200' };
      case 'STATEMENT':
        return { icon: 'ri-money-cny-box-line', bg: 'bg-emerald-50 text-emerald-600 border-emerald-200' };
      case 'SETTLEMENT':
        return { icon: 'ri-bank-card-line', bg: 'bg-amber-50 text-amber-600 border-amber-200' };
      default:
        return { icon: 'ri-notification-3-line', bg: 'bg-gray-50 text-gray-600 border-gray-200' };
    }
  };

  const hasMore = items.length < total;

  return (
    <div className="flex flex-col h-full">
      {/* 头部控制卡片 */}
      <div className="flex items-center justify-between pb-3 mb-4 border-b border-background-100 shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center text-sm">
            <i className="ri-history-line"></i>
          </div>
          <div>
            <h3 className="font-bold text-foreground-950 text-sm flex items-center gap-1.5">
              项目进度时间线
            </h3>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          <Select
            options={TYPE_OPTIONS}
            value={typeFilter}
            onChange={handleTypeChange}
            size="sm"
            className="w-32"
          />
          <button
            onClick={handleRefresh}
            title="刷新时间线"
            className="p-1.5 rounded-md border border-gray-200 text-foreground-500 hover:text-primary-600 hover:bg-gray-50 text-xs transition-colors shrink-0"
          >
            <i className={`ri-refresh-line ${isLoading && page === 1 ? 'animate-spin' : ''}`}></i>
          </button>
        </div>
      </div>

      {/* 时间线列表展示区 (滚动触底自动加载) */}
      <div
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto max-h-[580px] pr-1.5 custom-scrollbar"
      >
        {isLoading && page === 1 ? (
          <div className="py-16 text-center text-foreground-400 text-xs">
            <i className="ri-loader-4-line animate-spin text-xl block mb-2 text-primary-500"></i>
            加载进度时间线...
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-foreground-400 text-xs bg-background-50/60 rounded-xl border border-background-200/50">
            <i className="ri-time-line text-3xl block mb-2 text-foreground-300"></i>
            暂无符合条件的时间线事件
          </div>
        ) : (
          <>
            <div className="relative pl-6 border-l-2 border-primary-200/80 space-y-4 my-2 ml-3">
              {items.map((event, idx) => {
                const style = getEventStyle(event.type);

                return (
                  <div key={event.id || idx} className="relative group">
                    {/* 定位在左轴上的微型圆圈图标 */}
                    <div className={`absolute -left-[37px] top-0.5 w-6 h-6 rounded-full border flex items-center justify-center text-xs shadow-2xs ${style.bg}`}>
                      <i className={style.icon}></i>
                    </div>

                    {/* 事件面板卡片 */}
                    <div className="p-3 rounded-xl bg-white border border-gray-200/70 hover:border-primary-300 hover:shadow-2xs transition-all">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <h4 className="font-bold text-foreground-900 text-xs">
                            {event.title || '进度事件'}
                          </h4>
                          {renderSeverityBadge(event.severity)}
                        </div>
                        <span className="text-[10px] text-foreground-400 font-mono shrink-0">
                          {formatDate(event.occurred_at)}
                        </span>
                      </div>

                      {event.description && (
                        <p className="text-xs text-foreground-600 leading-normal mt-1">
                          {event.description}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 滚动触底加载状态指示器 */}
            <div className="py-3 text-center text-xs text-foreground-400">
              {isLoading && page > 1 ? (
                <span className="flex items-center justify-center gap-1.5 text-primary-600">
                  <i className="ri-loader-4-line animate-spin"></i> 正在加载更多事件...
                </span>
              ) : hasMore ? (
                <span className="text-foreground-400 text-[11px]">向下滚动加载更多</span>
              ) : (
                <span className="text-foreground-400 text-[11px]">已加载全部事件 (共 {total} 条)</span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

